// Security → Inventory (migration 20261024150000).
//
// Every GitHub repo and Netlify site, sorted keep / junk / unsure by
// scripts/infra-inventory.mjs collect. The app only records a person's decision;
// `infra-inventory.mjs execute` carries it out with tokens Cockpit never holds.

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseClient = SupabaseClient<any, 'public', any>;
const db = supabase as unknown as LooseClient;

export type InventoryVerdict = 'keep' | 'junk' | 'unsure';
export type InventoryDecision = 'keep' | 'archive' | 'delete';

export type InventoryDomain = { domain: string; status?: number | string; live?: boolean; dns?: string | null };
export type InventorySite = { id: string; name: string; url: string | null; domains: string[]; repo: string | null; published_at: string | null };

export type InventoryFacts = {
  owner?: string;
  owned?: boolean;
  private?: boolean;
  archived?: boolean;
  fork?: boolean;
  pushed_at?: string | null;
  created_at?: string | null;
  description?: string | null;
  language?: string | null;
  sites?: InventorySite[];
  domains?: InventoryDomain[];
  // site rows
  repo?: string | null;
  published_at?: string | null;
  manual_deploy?: boolean;
};

export type InventoryRow = {
  id: string;
  kind: 'repo' | 'site';
  key: string;
  name: string;
  url: string | null;
  verdict: InventoryVerdict;
  verdict_reason: string;
  facts: InventoryFacts;
  client_name: string | null;
  decision: InventoryDecision | null;
  decision_note: string | null;
  decided_at: string | null;
  executed_at: string | null;
  execution_result: string | null;
  seen_at: string;
};

export const DECISION_LABEL: Record<InventoryDecision, string> = {
  keep: 'Keep',
  archive: 'Archive',
  delete: 'Delete',
};

/** What a decision will do, in words, for the row's side panel. */
export function decisionEffect(row: InventoryRow, decision: InventoryDecision): string {
  if (decision === 'keep') return 'Nothing changes.';
  if (row.kind === 'site') return 'Deletes this Netlify site and its deploys. Cannot be undone.';
  if (decision === 'archive') return `${row.facts.private ? '' : 'Makes the repo private, then '}archives it on GitHub. Reversible. Netlify sites stay.`;
  const sites = row.facts.sites ?? [];
  return `Deletes the repo${sites.length ? ` and ${sites.length === 1 ? 'its Netlify site' : `its ${sites.length} Netlify sites`} (${sites.map((s) => s.name).join(', ')})` : ''}. Cannot be undone.`;
}

/** Decisions the row can take: sites can't be archived, other owners' repos can only be kept. */
export function allowedDecisions(row: InventoryRow): InventoryDecision[] {
  if (row.kind === 'site') return ['keep', 'delete'];
  if (row.facts.owned === false) return ['keep'];
  return ['keep', 'archive', 'delete'];
}

/** Where the project runs: a custom domain that answers, else any custom domain, else the Netlify URL. */
export function liveUrl(row: InventoryRow): string | null {
  const domains = row.facts.domains ?? [];
  const domain = domains.find((d) => d.live) ?? domains[0];
  if (domain) return `https://${domain.domain}`;
  if (row.kind === 'site') return row.url;
  return row.facts.sites?.find((s) => s.url)?.url ?? null;
}

/** Last push or deploy, whichever is newer. */
export function lastActivity(row: InventoryRow): string | null {
  const dates = [row.facts.pushed_at, row.facts.published_at, ...(row.facts.sites ?? []).map((s) => s.published_at)].filter(Boolean) as string[];
  return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : null;
}

export async function fetchInventory(): Promise<InventoryRow[]> {
  const { data, error } = await db.from('infra_inventory').select('*').is('gone_at', null).order('name');
  if (error) throw error;
  return (data ?? []) as InventoryRow[];
}

export async function setDecisions(ids: string[], decision: InventoryDecision | null, userId: string | null): Promise<void> {
  const { data, error } = await db
    .from('infra_inventory')
    .update({ decision, decided_by: decision ? userId : null, decided_at: decision ? new Date().toISOString() : null })
    .in('id', ids)
    .is('executed_at', null)
    .select('id');
  if (error) throw error;
  // RLS refuses an update silently: zero rows back is the only sign.
  if ((data ?? []).length === 0) throw new Error('Nothing was saved. Only admins can decide, and finished rows are locked.');
}

export async function setDecisionNote(id: string, note: string | null): Promise<void> {
  const { error } = await db.from('infra_inventory').update({ decision_note: note }).eq('id', id);
  if (error) throw error;
}
